/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Line } from '@react-three/drei';
import { ThreeEvent } from '@react-three/fiber';
import { MeasureKind, UnitsByMeasure } from 'convert';
import polylabel from 'polylabel';
import React, { useEffect, useMemo, useState } from 'react';
import * as THREE from 'three';

import { EInteractionMode, EUnitType, EWallMode } from '../../@types/Enums';
import AttributeModel from '../../models/standard/AttributeModel';
import FloorModel from '../../models/standard/FloorModel';
import RoomModel from '../../models/standard/RoomModel';
import SpaceModel from '../../models/standard/SpaceModel';
import { DefaultAttributeId } from '../../utils/attributes/defaultObjectsAndAttributes';

import { EAttributeType, EAttributeUnit } from '../../@types/AttributeTypes';
import { useBuilding } from '../../provider/BuildingProvider/BuildingProvider';
import { useDraggingContext } from '../../provider/DraggingProvider';
import { useDrawing } from '../../provider/DrawingProvider';
import { useMultiSelectToggleContext } from '../../provider/MultiSelectToggleProvider';
import { useToolbarContext } from '../../provider/ToolbarProvider';
import { useUnitContext } from '../../provider/UnitProvider';
import { useValidation } from '../../provider/ValidationProvider';
import { CM_TO_M_DIVISOR, DEFAULT_FLOOR_COLOR, INVALID_TILE_OPACITY, Z_AXIS_OFFSET } from '../../utils/constants';
import { convertTo } from '../../utils/conversionUtils';
import RENDER_ORDERS from '../../utils/renderOrder';
import { calculatePolygonArea, calculatePolygonCentroid } from '../../utils/vectorUtils';
import RoomDetailsIndicator from './indicators/RoomDetailsIndicator';
import TilesTexture from './TilesTexture';

interface FloorProps {
  shape: THREE.Shape;
  selectedRoom: boolean;
  onClick?: () => void;
  floorModel: FloorModel | null | undefined;
  roomModel: RoomModel | null | undefined;
  writeGeometryAttributes?: boolean;
  spaceId?: string;
}

const FLOOR_HOVER_DARKEN_RATIO = 0.75;
const TEXTURE_BASE_TINT_COLOR = '#FFFFFF';
const TEXTURE_HOVER_TINT_COLOR = `#${new THREE.Color(TEXTURE_BASE_TINT_COLOR).multiplyScalar(FLOOR_HOVER_DARKEN_RATIO).getHexString()}`;


const getHoverFloorColor = (color: string): string => {
  try {
    return `#${new THREE.Color(color).multiplyScalar(FLOOR_HOVER_DARKEN_RATIO).getHexString()}`;
  } catch {
    return '#CCCCCC';
  }
};




const ROOM_LABEL_POINT_PRECISION = 0.1;
const ROOM_LABEL_BLOCK_HALF_WIDTH = 4.2;
const ROOM_LABEL_BLOCK_HALF_HEIGHT = 1.8;
const ROOM_LABEL_CANDIDATE_GRID_STEPS = 8;

const ROOM_LABEL_BLOCK_OFFSETS = [
  { x: 0, y: 0 },
  { x: -ROOM_LABEL_BLOCK_HALF_WIDTH, y: -ROOM_LABEL_BLOCK_HALF_HEIGHT },
  { x: 0, y: -ROOM_LABEL_BLOCK_HALF_HEIGHT },
  { x: ROOM_LABEL_BLOCK_HALF_WIDTH, y: -ROOM_LABEL_BLOCK_HALF_HEIGHT },
  { x: -ROOM_LABEL_BLOCK_HALF_WIDTH, y: 0 },
  { x: ROOM_LABEL_BLOCK_HALF_WIDTH, y: 0 },
  { x: -ROOM_LABEL_BLOCK_HALF_WIDTH, y: ROOM_LABEL_BLOCK_HALF_HEIGHT },
  { x: 0, y: ROOM_LABEL_BLOCK_HALF_HEIGHT },
  { x: ROOM_LABEL_BLOCK_HALF_WIDTH, y: ROOM_LABEL_BLOCK_HALF_HEIGHT },
];

const getOpenPolygonPoints = (polygon: THREE.Vector2[]): THREE.Vector2[] => {
  if (polygon.length < 2) return polygon;

  const points = polygon.reduce<THREE.Vector2[]>((result, point) => {
    const previous = result[result.length - 1];

    if (!previous || Math.abs(previous.x - point.x) >= 1e-6 || Math.abs(previous.y - point.y) >= 1e-6) {
      result.push(point);
    }

    return result;
  }, []);

  const first = points[0];
  const last = points[points.length - 1];
  const isClosed = first && last && Math.abs(first.x - last.x) < 1e-6 && Math.abs(first.y - last.y) < 1e-6;

  return isClosed ? points.slice(0, -1) : points;
};

const isPointOnPolygonSegment = (point: { x: number; y: number }, polygon: THREE.Vector2[]): boolean => {
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[j];
    const b = polygon[i];
    const abx = b.x - a.x;
    const aby = b.y - a.y;
    const apx = point.x - a.x;
    const apy = point.y - a.y;
    const cross = abx * apy - aby * apx;

    if (Math.abs(cross) > 1e-6) continue;

    const dot = apx * abx + apy * aby;
    const lengthSq = abx * abx + aby * aby;

    if (dot >= -1e-6 && dot <= lengthSq + 1e-6) return true;
  }

  return false;
};

const isPointInsidePolygon = (point: { x: number; y: number }, polygon: THREE.Vector2[]): boolean => {
  if (isPointOnPolygonSegment(point, polygon)) return true;

  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const current = polygon[i];
    const previous = polygon[j];

    const intersects = current.y > point.y !== previous.y > point.y
      && point.x < ((previous.x - current.x) * (point.y - current.y)) / (previous.y - current.y) + current.x;

    if (intersects) inside = !inside;
  }

  return inside;
};

const isRoomLabelBlockInsidePolygon = (point: { x: number; y: number }, polygon: THREE.Vector2[]): boolean =>
  ROOM_LABEL_BLOCK_OFFSETS.every((offset) => isPointInsidePolygon({ x: point.x + offset.x, y: point.y + offset.y }, polygon));

const getDistanceToPolygonEdges = (point: { x: number; y: number }, polygon: THREE.Vector2[]): number => {
  let closestDistanceSq = Infinity;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[j];
    const b = polygon[i];
    const abx = b.x - a.x;
    const aby = b.y - a.y;
    const lenSq = abx * abx + aby * aby;

    if (lenSq <= 1e-12) continue;

    const t = Math.max(0, Math.min(1, ((point.x - a.x) * abx + (point.y - a.y) * aby) / lenSq));
    const qx = a.x + abx * t;
    const qy = a.y + aby * t;
    const dx = point.x - qx;
    const dy = point.y - qy;

    closestDistanceSq = Math.min(closestDistanceSq, dx * dx + dy * dy);
  }

  return Number.isFinite(closestDistanceSq) ? Math.sqrt(closestDistanceSq) : 0;
};

const getPolygonBounds = (polygon: THREE.Vector2[]) => {
  let minX = polygon[0].x;
  let maxX = polygon[0].x;
  let minY = polygon[0].y;
  let maxY = polygon[0].y;

  polygon.forEach((point) => {
    minX = Math.min(minX, point.x);
    maxX = Math.max(maxX, point.x);
    minY = Math.min(minY, point.y);
    maxY = Math.max(maxY, point.y);
  });

  return { minX, maxX, minY, maxY };
};

const findRoomLabelPointThatFitsBlock = (
  polygon: THREE.Vector2[],
  preferredPoint: { x: number; y: number },
  fallbackPoint: { x: number; y: number },
): { x: number; y: number } | null => {
  const candidates: Array<{ x: number; y: number }> = [preferredPoint];

  for (let i = 1; i <= 4; i++) {
    const t = i / 4;
    candidates.push({
      x: preferredPoint.x + (fallbackPoint.x - preferredPoint.x) * t,
      y: preferredPoint.y + (fallbackPoint.y - preferredPoint.y) * t,
    });
  }

  const bounds = getPolygonBounds(polygon);

  for (let ix = 0; ix <= ROOM_LABEL_CANDIDATE_GRID_STEPS; ix++) {
    for (let iy = 0; iy <= ROOM_LABEL_CANDIDATE_GRID_STEPS; iy++) {
      candidates.push({
        x: bounds.minX + ((bounds.maxX - bounds.minX) * ix) / ROOM_LABEL_CANDIDATE_GRID_STEPS,
        y: bounds.minY + ((bounds.maxY - bounds.minY) * iy) / ROOM_LABEL_CANDIDATE_GRID_STEPS,
      });
    }
  }

  let bestCandidate: { x: number; y: number } | null = null;
  let bestScore = Infinity;

  candidates.forEach((candidate) => {
    if (!isRoomLabelBlockInsidePolygon(candidate, polygon)) return;

    const preferredDistance = Math.hypot(candidate.x - preferredPoint.x, candidate.y - preferredPoint.y);
    const edgeDistance = getDistanceToPolygonEdges(candidate, polygon);
    const score = preferredDistance - edgeDistance * 0.15;

    if (score < bestScore) {
      bestScore = score;
      bestCandidate = candidate;
    }
  });

  return bestCandidate;
};

const getRoomLabelPoint = (polygon: THREE.Vector2[]): { x: number; y: number } => {
  const openPolygon = getOpenPolygonPoints(polygon);

  if (openPolygon.length === 0) return { x: 0, y: 0 };
  if (openPolygon.length < 3) return { x: openPolygon[0].x, y: openPolygon[0].y };

  const centroid = calculatePolygonCentroid(openPolygon);

  const ring: number[][] = openPolygon.map((point) => [point.x, point.y]);
  ring.push([...ring[0]]);

  const [polylabelX, polylabelY] = polylabel([ring], ROOM_LABEL_POINT_PRECISION);
  const fallbackPoint = { x: polylabelX, y: polylabelY };
  const hasValidCentroid = Number.isFinite(centroid.x) && Number.isFinite(centroid.y) && isPointInsidePolygon(centroid, openPolygon);
  const preferredPoint = hasValidCentroid ? { x: centroid.x, y: centroid.y } : fallbackPoint;

  if (isRoomLabelBlockInsidePolygon(preferredPoint, openPolygon)) return preferredPoint;

  return findRoomLabelPointThatFitsBlock(openPolygon, preferredPoint, fallbackPoint) ?? fallbackPoint;
};

const Floor = ({ shape, selectedRoom, onClick, floorModel, roomModel, writeGeometryAttributes = true, spaceId }: FloorProps): React.JSX.Element => {
  const { hasIntersectingWalls } = useValidation();
  const { showRoomDetails } = useMultiSelectToggleContext();
  const { wallMode, interactionMode, setInteractionMode } = useToolbarContext();

  const { applyTexture, setRoomPerimeter, setBuilding } = useBuilding();

  const { isDragging } = useDraggingContext();
  const { roomCompleted } = useDrawing();
  const { unitType } = useUnitContext();

  const [hovered, setHovered] = useState(false);
  const [tilesTexture, setTilesTexture] = useState<THREE.CanvasTexture>();

  const tiles = floorModel?.tiles ?? undefined;

  // ---------------------------------------------------------------------------
  // 1. Geometry Hash
  // ---------------------------------------------------------------------------
  const geometryHash = useMemo(() => shape.getPoints().reduce((acc, p) => acc + `${p.x.toFixed(2)},${p.y.toFixed(2)}|`, ''), [shape, isDragging]);

  const hasValidTiles = useMemo(() => {
    if (!tiles) return false;
    const w = Number((tiles as any).width);
    const l = Number((tiles as any).length);
    return Number.isFinite(w) && Number.isFinite(l) && w > 0 && l > 0;
  }, [tiles]);

  useEffect(() => {
    if (!hasValidTiles) setTilesTexture(undefined);
  }, [hasValidTiles]);

  const textureColor = floorModel?.texture?.color;

  const baseColor = useMemo(() => {
    if (hasIntersectingWalls) return 'red';
    const normalColor = textureColor || DEFAULT_FLOOR_COLOR;
    return hovered && !selectedRoom ? getHoverFloorColor(normalColor) : normalColor;
  }, [hasIntersectingWalls, hovered, selectedRoom, textureColor]);

  const textureTintColor = useMemo(() => {
    if (hasIntersectingWalls) return 'red';
    return hovered && !selectedRoom ? TEXTURE_HOVER_TINT_COLOR : TEXTURE_BASE_TINT_COLOR;
  }, [hasIntersectingWalls, hovered, selectedRoom]);

  const shapePointsV2 = useMemo(() => shape.getPoints().map((p) => new THREE.Vector2(p.x, p.y)), [shape, geometryHash]);
  const roomArea = useMemo(() => Math.round(calculatePolygonArea(shapePointsV2)) / CM_TO_M_DIVISOR, [shapePointsV2, geometryHash]);

  const memoizedRoomPerimeter = useMemo(() => {
    const pts = shape.getPoints();
    if (pts.length < 2) return '0';
    let length = 0;
    for (let i = 0; i < pts.length; i++) {
      const a = pts[i];
      const b = pts[(i + 1) % pts.length];
      length += Math.hypot(b.x - a.x, b.y - a.y);
    }
    length /= 10;
    return length.toFixed(2);
  }, [shape, geometryHash]);


  useEffect(() => {
    if (!writeGeometryAttributes) return;
    if (isDragging) return;

    const targetRoomUuid = roomModel?.uuid;
    const targetSpaceUuid = spaceId;

    if (!targetRoomUuid || !targetSpaceUuid) return;

    let area = roomArea;
    let perimeter = Number(memoizedRoomPerimeter);
    const areaUnit = unitType === EUnitType.METRIC ? EAttributeUnit.M_2 : EAttributeUnit.FT_2;
    const perimeterUnit = unitType === EUnitType.METRIC ? EAttributeUnit.M : EAttributeUnit.FT;

    if (unitType === EUnitType.IMPERIAL) {
      area = convertTo(area, 'm2' as UnitsByMeasure<MeasureKind.Area>, 'ft2' as UnitsByMeasure<MeasureKind.Area>);
      perimeter = convertTo(perimeter, 'meters' as UnitsByMeasure<MeasureKind.Length>, 'feet' as UnitsByMeasure<MeasureKind.Length>);
    }

    const nextArea = Number(area.toFixed(3));
    const nextPer = Number(perimeter.toFixed(3));

    setBuilding((prev) => {
      const rooms = Array.isArray(prev.rooms) ? [...prev.rooms] : [];
      
      const rIdx = rooms.findIndex((r) => (r as any)?.uuid === targetRoomUuid);
      if (rIdx < 0) return prev; 

      const oldRoom = rooms[rIdx] as unknown as RoomModel;
      const oldSpaces = (oldRoom.spaces ?? []) as SpaceModel[];

      const sIdx = oldSpaces.findIndex((s) => (s as any)?.uuid === targetSpaceUuid);
      if (sIdx < 0) return prev;
      
      const oldSpace = oldSpaces[sIdx];
      const oldFloor = oldSpace.floor as FloorModel | undefined;
      
      const currentArea = oldFloor?.attributes?.find((a) => a.id === DefaultAttributeId.FLOOR_AREA)?.value;
      const currentPerim = oldFloor?.attributes?.find((a) => a.id === DefaultAttributeId.FLOOR_PERIMETER)?.value;
      
      if (currentArea === nextArea && currentPerim === nextPer) {
        return prev;
      }

      let nextFloor = oldFloor;
      if (oldFloor) {
        const oldFAttrs = oldFloor.attributes ?? [];
        const updatedF = oldFAttrs.map((a) => {
          if (a.id === DefaultAttributeId.FLOOR_AREA) {
            return new AttributeModel(a.id, a.key, nextArea, a.predecessorId, EAttributeType.NUMBER, areaUnit, a.hidden, a.forced, a.ownerId, a.userId, a.publicFlag, a.localization, a.defaults, a.validation, a.unitFamily);
          }
          if (a.id === DefaultAttributeId.FLOOR_PERIMETER) {
            return new AttributeModel(a.id, a.key, nextPer, a.predecessorId, EAttributeType.NUMBER, perimeterUnit, a.hidden, a.forced, a.ownerId, a.userId, a.publicFlag, a.localization, a.defaults, a.validation, a.unitFamily);
          }
          return a;
        });

        nextFloor = new FloorModel(oldFloor.tiles, oldFloor.texture, updatedF, oldFloor.uuid);
        if (oldFloor.floorShape) nextFloor.setFloorShape(oldFloor.floorShape);
      } else {
         return prev;
      }

      const newSpace = new SpaceModel(nextFloor, oldSpace.products, oldSpace.elements, oldSpace.models2D, oldSpace.boundaries, oldSpace.walls);
      if (oldSpace.uuid) newSpace.setUUID(oldSpace.uuid);

      const newSpaces = oldSpaces.slice();
      newSpaces[sIdx] = newSpace;

      const oldRAttrs = oldRoom.attributes ?? [];
      const nextRAttrs = oldRAttrs.map((a) => {
        if (a.id === DefaultAttributeId.ROOM_AREA) {
          return new AttributeModel(a.id, a.key, nextArea, a.predecessorId, EAttributeType.NUMBER, areaUnit, a.hidden, a.forced, a.ownerId, a.userId, a.publicFlag, a.localization, a.defaults, a.validation, a.unitFamily);
        }
        if (a.id === DefaultAttributeId.ROOM_PERIMETER) {
          return new AttributeModel(a.id, a.key, nextPer, a.predecessorId, EAttributeType.NUMBER, perimeterUnit, a.hidden, a.forced, a.ownerId, a.userId, a.publicFlag, a.localization, a.defaults, a.validation, a.unitFamily);
        }
        return a;
      });

      const newRoom = new RoomModel(oldRoom.title, newSpaces, nextRAttrs);
      if ((oldRoom as any).uuid) newRoom.setUUID((oldRoom as any).uuid);
      
      rooms[rIdx] = newRoom;
      
      return { ...prev, rooms };
    });
  }, [
    writeGeometryAttributes, 
    roomCompleted, 
    isDragging, 
    roomModel?.uuid,
    spaceId,        
    roomArea,      
    memoizedRoomPerimeter, 
    unitType, 
    setBuilding
  ]);

  useEffect(() => {
    if (!writeGeometryAttributes) return;
    if (roomCompleted) setRoomPerimeter(Number.parseFloat(memoizedRoomPerimeter));
  }, [memoizedRoomPerimeter, setRoomPerimeter, roomCompleted, writeGeometryAttributes]);

  useEffect(() => {
    const material = floorModel?.texture?.material;
    if (material) {
      if (floorModel?.texture?.offset !== undefined) material.offset.x = -floorModel.texture.offset;
      if (floorModel?.texture?.rotation !== undefined) material.rotation = -floorModel.texture.rotation;
    }
  }, [floorModel?.texture, floorModel?.texture?.material]);

  const outlineVertices = useMemo(() => shape.getPoints().map((p) => new THREE.Vector3(p.x, p.y, Z_AXIS_OFFSET)), [shape, geometryHash]);
  const centroid: { x: number; y: number } = useMemo(() => {
    if (!showRoomDetails) return { x: 0, y: 0 };

    return getRoomLabelPoint(shape.getPoints().map((p) => new THREE.Vector2(p.x, p.y)));
  }, [shape, geometryHash, showRoomDetails]);

  const textureToUse = useMemo(() => (floorModel?.texture?.material ? floorModel.texture.material : tilesTexture ?? undefined), [tilesTexture, floorModel?.texture?.material]);

  const handleFloorClick = (event: ThreeEvent<PointerEvent>) => {
    if (event.button !== 0 || isDragging || wallMode === EWallMode.PARTITION) return;
    if (interactionMode === EInteractionMode.MOVE) return;

    if (interactionMode === EInteractionMode.SET_TEXTURE && onClick && floorModel) {
      applyTexture(floorModel);
      setInteractionMode(EInteractionMode.NONE);
      onClick();
    } else if (onClick) {
      onClick();
    }
  };

  const isOpaque = !!(textureColor || hasValidTiles || textureToUse);

  return (
    <group name="group-floor">
      {hasValidTiles && tiles && (
        <TilesTexture
          width={(tiles as any).width}
          length={(tiles as any).length}
          jointColor={(tiles as any).jointColor}
          tileColor={textureColor || DEFAULT_FLOOR_COLOR}
          jointWidth={(tiles as any).jointWidth}
          rotation={(tiles as any).rotation}
          onTextureUpdate={setTilesTexture}
        />
      )}

      <mesh
        name="floor-mesh"
        onPointerDown={handleFloorClick}
        userData={{ isFloor: true, spaceUuid: spaceId }}
        onPointerEnter={() => !isDragging && setHovered(true)}
        onPointerLeave={() => setHovered(false)}
        renderOrder={RENDER_ORDERS.floor}
      >
        <shapeGeometry attach="geometry" args={[shape]} name="floor-mesh-shapeGeometry" />
        <meshBasicMaterial
          key={textureToUse ? textureToUse.id : 'no-texture'}
          transparent={!isOpaque}
          opacity={isOpaque ? 1.0 : INVALID_TILE_OPACITY}
          depthWrite={isOpaque}
          attach="material"
          color={textureToUse ? textureTintColor : baseColor}
          map={textureToUse}
          side={THREE.FrontSide}
          name="floor-mesh-meshBasicMaterial"
        />
      </mesh>

      {selectedRoom && <Line name="floor-line" points={outlineVertices} color="#009fe3" lineWidth={10} renderOrder={RENDER_ORDERS.outline} />}

      {showRoomDetails && roomModel && (
        <RoomDetailsIndicator
          roomName={roomModel.title}
          roomArea={roomArea}
          roomPerimeter={Number.parseFloat(memoizedRoomPerimeter)}
          centroid={centroid}
          roomNameOffset={roomModel.title ? -1 : 0}
        />
      )}
    </group>
  );
};

export default Floor;
